import React, { useCallback } from 'react';
import { type Href, useRouter } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';

import { useResponsive, VStack } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry, useTrackEventOnFocus } from '@order/Telemetry';

import { useContactUsHandler } from '../../../../../hooks/useContactUsHandler';
import { useIsChatAppEnabled } from '../../../../../hooks/useIsChatAppEnabled';
import { isIdMeConfigured } from '../../../../../integrations/idme/config';
import { AccountMenuLink } from '../AccountMenuLink';
import { AccountMenuPrimaryItem } from '../AccountMenuPrimaryItem';

import type { AccountTabScreenId } from '../../../Account.types';

// ─────────────────────────────────────────────────────────────────────────────

export const AccountPrimaryMenu = (props: Props) => {
  const { screenId, ...stackProps } = props;

  const router = useRouter();
  const { formatMessage } = useIntl();
  const { track } = useTelemetry();
  const { handleContactUs } = useContactUsHandler('account');
  const { currentBreakpoint } = useResponsive();

  // ─── Flags ───────────────────────────────────────────────────────────

  const isChatAppEnabled = useIsChatAppEnabled();
  const isFlexEnabled = useFeatureFlag('CELS-5522-flex-enabled');
  const isIdMeVerificationEnabled = useFeatureFlag('CELS-6174-order-id-me-verification-enabled');

  const isIdMeAvailable = isIdMeVerificationEnabled && isIdMeConfigured;

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleRewardsAndOffersPress = useCallback(() => {
    track('account_rewards_offers_selected');
  }, [track]);

  const goToForgotToScan = useCallback(() => {
    track('forgot-to-scan.cta_tapped', { location: 'account' });

    router.navigate('/forgot-to-scan');
  }, [router, track]);

  const handleIdMePress = useCallback(() => {
    track('idme.nav_entry.tapped');
  }, [track]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useTrackEventOnFocus({ name: 'idme.nav_entry.viewed' }, { skip: !isIdMeAvailable });

  // ─────────────────────────────────────────────────────────────────────

  return (
    <VStack {...stackProps}>
      {currentBreakpoint.isXS ? (
        <AccountMenuLink
          testID="account.sidebar-tab.rewards"
          label={formatMessage(messages.rewardsAndOffers)}
          iconName="IconRewardStroke"
          href="/loyalty"
          onPress={handleRewardsAndOffersPress}
        />
      ) : null}

      <AccountMenuLink
        testID="account.sidebar-tab.profile"
        label={formatMessage(messages.accountInfo)}
        iconName="IconProfileStroke"
        href="/account/info"
        active={screenId === 'profile'}
      />

      <AccountMenuLink
        label={formatMessage(messages.payments)}
        iconName="IconCreditCard"
        href="/account/payments"
        active={screenId === 'payments'}
      />

      <AccountMenuLink
        label={formatMessage(messages.creditAndPromoCodes)}
        iconName="IconBadgeScan"
        href="/account/credit-and-promo-codes"
        active={screenId === 'credit-and-promo-codes'}
      />

      <AccountMenuLink
        testID="account.sidebar-tab.gift-cards"
        label={formatMessage(messages.giftCards)}
        iconName="IconRewardStroke"
        href="/account/gift-cards"
        active={screenId === 'gift-cards'}
      />

      {isFlexEnabled ? (
        <AccountMenuLink
          testID="account.sidebar-tab.flex"
          label={formatMessage(messages.hsaFsa)}
          iconName="IconFlex"
          href="/hsa-fsa"
        />
      ) : null}

      {isIdMeAvailable ? (
        <AccountMenuLink
          testID="account.sidebar-tab.id-me"
          label={formatMessage(messages.idMe)}
          iconName="IconIdMe"
          href={ID_ME_HREF}
          active={screenId === 'id-me'}
          onPress={handleIdMePress}
        />
      ) : null}

      <AccountMenuPrimaryItem
        testID="account.sidebar-tab.forgot-to-scan"
        label={formatMessage(messages.forgotToScan)}
        iconName="IconAccountForgotToScan"
        onPress={goToForgotToScan}
      />

      <AccountMenuLink
        testID="account.sidebar-tab.addresses"
        label={formatMessage(messages.addresses)}
        iconName="IconPickupStrokeSm"
        href="/account/addresses"
        active={screenId === 'addresses'}
      />

      <AccountMenuLink
        testID="account.sidebar-tab.orders"
        label={formatMessage(messages.orders)}
        iconName="IconBowl"
        href="/account/orders"
        active={screenId === 'orders'}
      />

      <AccountMenuLink
        testID="account.sidebar-tab.favorites"
        label={formatMessage(messages.favorites)}
        iconName="IconHeartStroke"
        href="/account/favorites"
        active={screenId === 'favorites'}
      />

      <AccountMenuLink
        testID="account.sidebar-tab.dietary-restrictions"
        label={formatMessage(messages.dietaryRestrictions)}
        iconName="IconFilter"
        href="/account/dietary-restrictions"
        active={screenId === 'dietary-restrictions'}
      />

      <AccountMenuLink
        label={formatMessage(messages.notifications)}
        iconName="IconBell"
        href="/account/notifications"
        active={screenId === 'notifications'}
      />

      {isChatAppEnabled ? (
        <AccountMenuPrimaryItem
          testID="account.sidebar-tab.contact-us"
          label={formatMessage(messages.contactUs)}
          iconName="IconText"
          onPress={handleContactUs}
        />
      ) : null}
    </VStack>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const ID_ME_HREF: Href = { pathname: '/account/id-me', params: { entry_source: 'side_nav' } };

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  rewardsAndOffers: {
    defaultMessage: 'Rewards and Offers',
    description: 'Account | Menu | Rewards and Offers',
  },
  accountInfo: {
    defaultMessage: 'Account info',
    description: 'Account | Menu | Account info',
  },
  creditAndPromoCodes: {
    defaultMessage: 'Credit & promo codes',
    description: 'Account | Menu | Credit & promo codes',
  },
  payments: {
    defaultMessage: 'Payments',
    description: 'Account | Menu | Payments',
  },
  addresses: {
    defaultMessage: 'Addresses',
    description: 'Account | Menu | Addresses',
  },
  giftCards: {
    defaultMessage: 'Gift cards',
    description: 'Account | Menu | Gift cards',
  },
  forgotToScan: {
    defaultMessage: 'Forgot to scan',
    description: 'Account | Menu | Forgot to scan',
  },
  hsaFsa: {
    defaultMessage: 'HSA/FSA',
    description: 'Account | Menu | HSA/FSA',
  },
  idMe: {
    defaultMessage: 'Verify with IDMe',
    description: 'Account | Menu | Verify with IDMe',
  },
  orders: {
    defaultMessage: 'Orders',
    description: 'Account | Menu | Orders',
  },
  favorites: {
    defaultMessage: 'Favorites',
    description: 'Account | Menu | Favorites',
  },
  dietaryRestrictions: {
    defaultMessage: 'Dietary restrictions',
    description: 'Account | Menu | Dietary restrictions',
  },
  notifications: {
    defaultMessage: 'Notifications',
    description: 'Account | Menu | Notifications',
  },
  contactUs: {
    defaultMessage: 'Contact us',
    description: 'Account | Menu | Contact us',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  screenId?: AccountTabScreenId;
} & Omit<React.ComponentProps<typeof VStack>, 'children'>;
